import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Leaf, X, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiInviteUser, isApiError } from '@/lib/api'

const invitationSchema = z.object({
  email: z.string().trim().email('Email invalide'),
  role: z.enum(['ADMIN', 'MEMBER']),
})

const schema = z.object({
  invitations: z.array(invitationSchema).min(1),
})

type FormValues = z.infer<typeof schema>

type RowStatus = 'idle' | 'pending' | 'success' | { error: string }

export type InviteMembersProps = {
  onComplete: () => void
}

// `Input` shadcn n'est pas forwardRef → toujours `Controller`, jamais
// `{...register()}` (cf. memory feedback_rhf_controller.md).
export function InviteMembers({ onComplete }: InviteMembersProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: { invitations: [{ email: '', role: 'MEMBER' }] },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'invitations' })

  const [rowStatus, setRowStatus] = useState<Record<number, RowStatus>>({})
  // shadcn `Input` n'est pas forwardRef → ref avalée. On focus l'input via son
  // `id` après que la nouvelle ligne soit montée (effet sur fields.length).
  const pendingFocusIndex = useRef<number | null>(null)

  useEffect(() => {
    if (pendingFocusIndex.current === null) return
    const id = `invite-email-${pendingFocusIndex.current}`
    const el = document.getElementById(id) as HTMLInputElement | null
    el?.focus()
    pendingFocusIndex.current = null
  }, [fields.length])

  const allProcessed = useMemo(
    () =>
      fields.length > 0 &&
      fields.every((_, i) => {
        const s = rowStatus[i]
        return s === 'success' || (typeof s === 'object' && s !== null && 'error' in s)
      }),
    [fields, rowStatus],
  )

  const onSubmit = async ({ invitations }: FormValues) => {
    // Marquer toutes les lignes "pending" (sauf celles déjà success — figées).
    setRowStatus((prev) => {
      const next: Record<number, RowStatus> = { ...prev }
      invitations.forEach((_, i) => {
        if (next[i] !== 'success') next[i] = 'pending'
      })
      return next
    })

    // N'envoyer que les lignes pas encore succès (retry des erreurs).
    const targets = invitations
      .map((row, index) => ({ row, index }))
      .filter(({ index }) => rowStatus[index] !== 'success')

    const results = await Promise.allSettled(
      targets.map(({ row }) => apiInviteUser({ email: row.email.trim(), role: row.role })),
    )

    setRowStatus((prev) => {
      const next: Record<number, RowStatus> = { ...prev }
      results.forEach((r, i) => {
        const { index } = targets[i]
        if (r.status === 'fulfilled') {
          next[index] = 'success'
        } else {
          const err = r.reason
          const msg = isApiError(err)
            ? err.message
            : err instanceof Error && err.message
              ? err.message
              : 'Erreur réseau'
          next[index] = { error: msg }
        }
      })
      return next
    })

    if (results.length > 0 && results.every((r) => r.status === 'rejected')) {
      toast.error('Impossible d\'envoyer les invitations — vérifiez votre connexion')
    }
  }

  const handleAppend = () => {
    pendingFocusIndex.current = fields.length
    append({ email: '', role: 'MEMBER' })
  }

  return (
    <div className="max-w-xl mx-auto w-full">
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 shadow-sm">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-green-700 text-lg leading-tight">SustainTrack</span>
            <span className="text-xs text-green-600 leading-tight">Invitez votre équipe</span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 bg-white/70 backdrop-blur-sm p-6 rounded-lg border shadow"
      >
        <div>
          <h2 className="text-base font-semibold">Invitez vos collaborateurs</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Ajoutez les emails de vos collaborateurs et choisissez leur rôle. Vous pourrez en inviter d'autres plus tard depuis les paramètres.
          </p>
        </div>

        <div className="space-y-3">
          {fields.map((field, index) => {
            const status = rowStatus[index] ?? 'idle'
            const isSuccess = status === 'success'
            const isPending = status === 'pending'
            const rowError = typeof status === 'object' && status !== null && 'error' in status ? status.error : null

            return (
              <div key={field.id} className="space-y-1">
                <div className="grid grid-cols-[1fr_140px_32px] gap-3 items-end">
                  <div>
                    {index === 0 && <Label htmlFor={`invite-email-${index}`}>Email</Label>}
                    <Controller
                      name={`invitations.${index}.email`}
                      control={control}
                      render={({ field: ctrl }) => (
                        <Input
                          id={`invite-email-${index}`}
                          type="email"
                          placeholder="collaborateur@entreprise.com"
                          value={ctrl.value}
                          onChange={ctrl.onChange}
                          onBlur={ctrl.onBlur}
                          readOnly={isSuccess}
                          disabled={isSuccess}
                          aria-invalid={Boolean(errors.invitations?.[index]?.email || rowError)}
                        />
                      )}
                    />
                  </div>
                  <div>
                    {index === 0 && <Label htmlFor={`invite-role-${index}`}>Rôle</Label>}
                    <Controller
                      name={`invitations.${index}.role`}
                      control={control}
                      render={({ field: ctrl }) => (
                        <select
                          id={`invite-role-${index}`}
                          value={ctrl.value}
                          onChange={ctrl.onChange}
                          onBlur={ctrl.onBlur}
                          disabled={isSuccess}
                          className="h-9 w-full border rounded px-2 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <option value="MEMBER">Membre</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-center h-9">
                    {isSuccess ? (
                      <Check className="h-4 w-4 text-green-600" aria-label="Invitation envoyée" />
                    ) : index > 0 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          remove(index)
                          // useFieldArray re-indexe les lignes ; on aligne
                          // rowStatus (clés numériques) en décalant tout
                          // ce qui était au-dessus de l'index supprimé.
                          setRowStatus((prev) => {
                            const next: Record<number, RowStatus> = {}
                            for (const [k, v] of Object.entries(prev)) {
                              const i = Number(k)
                              if (i < index) next[i] = v
                              else if (i > index) next[i - 1] = v
                              // i === index → drop
                            }
                            return next
                          })
                        }}
                        aria-label="Supprimer cette ligne"
                        disabled={isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
                {errors.invitations?.[index]?.email?.message && (
                  <p className="text-sm text-red-600">
                    {errors.invitations[index]?.email?.message as string}
                  </p>
                )}
                {rowError && (
                  <p className="text-sm text-red-600">{rowError}</p>
                )}
                {isPending && (
                  <p className="text-xs text-muted-foreground">Envoi en cours…</p>
                )}
                {isSuccess && (
                  <p className="text-xs text-green-700">Invitation envoyée</p>
                )}
              </div>
            )
          })}
        </div>

        <div>
          <Button
            type="button"
            variant="outline"
            onClick={handleAppend}
            disabled={isSubmitting}
          >
            + Ajouter un collaborateur
          </Button>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          {allProcessed ? (
            <Button
              type="button"
              onClick={onComplete}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Continuer
            </Button>
          ) : (
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={isSubmitting || fields.length === 0}
            >
              {isSubmitting ? 'Envoi en cours…' : 'Envoyer les invitations'}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={onComplete}
            disabled={isSubmitting}
            className="w-full"
          >
            Passer cette étape
          </Button>
        </div>
      </form>
    </div>
  )
}
